import { BorderRadius, Color, Spacing, zIndex } from '@billy/design-system'
import styled from '@emotion/styled'

const LINE_ITEMS_ACTION_COLUMN_WIDTH = '40px'
const LINE_ITEMS_GRID_TEMPLATE = `minmax(96px, 1.4fr) minmax(104px, 1.5fr) minmax(88px, 0.6fr) repeat(3, minmax(72px, 0.8fr)) ${LINE_ITEMS_ACTION_COLUMN_WIDTH}`
const LINE_ITEMS_TABLE_MAX_HEIGHT = '40vh'
const LINE_ITEMS_TABLE_MIN_WIDTH = '544px'
const NARROW_TABLE_QUERY = '@container (max-width: 720px)'

export const ReviewEntryLineItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  container-type: inline-size;
`

export const ReviewEntryLineItemsScrollWrapper = styled.div`
  display: grid;
  row-gap: ${Spacing.S32};
  grid-template-columns: minmax(${LINE_ITEMS_TABLE_MIN_WIDTH}, 1fr);
  width: 100%;
  overflow-x: auto;
`

// Vertical scroll stays on the table; horizontal scroll stays on ScrollWrapper so totals stay aligned.
export const ReviewEntryLineItemsTable = styled.div`
  min-width: ${LINE_ITEMS_TABLE_MIN_WIDTH};
  max-height: ${LINE_ITEMS_TABLE_MAX_HEIGHT};
  border: 1px solid ${Color.Grey200};
  border-radius: ${BorderRadius.R8};
  background-color: ${Color.White};
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
`

export const ReviewEntryLineItemsHeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${LINE_ITEMS_GRID_TEMPLATE};
  min-height: 48px;
  border-bottom: 1px solid ${Color.Grey200};
  position: sticky;
  top: 0;
  z-index: ${zIndex.TableStickyHeader};
  background-color: ${Color.White};
`

interface ReviewEntryLineItemsHeaderCellProps {
  alignment?: 'left' | 'right'
}

export const ReviewEntryLineItemsHeaderCell = styled.div<ReviewEntryLineItemsHeaderCellProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ alignment }) => (alignment === 'right' ? 'flex-end' : 'flex-start')};
  min-width: 0;
  padding: ${Spacing.S8};
  text-align: ${({ alignment }) => alignment ?? 'left'};
  white-space: nowrap;
  overflow: hidden;

  ${NARROW_TABLE_QUERY} {
    padding: ${Spacing.S4};
  }
`

export const ReviewEntryLineItemActionWrapper = styled.div`
  display: flex;
  opacity: 0;
  align-items: center;
  justify-content: center;
  padding: 0 ${Spacing.S4};
  pointer-events: none;
`

export const ReviewEntryLineItemRow = styled.div`
  display: grid;
  grid-template-columns: ${LINE_ITEMS_GRID_TEMPLATE};
  min-height: 48px;
  border-bottom: 1px solid ${Color.Grey200};

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &:focus-within {
    ${ReviewEntryLineItemActionWrapper} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`

interface ReviewEntryLineItemCellProps {
  alignment?: 'left' | 'right'
}

export const ReviewEntryLineItemCell = styled.div<ReviewEntryLineItemCellProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ alignment }) => (alignment === 'right' ? 'flex-end' : 'flex-start')};
  min-width: 0;
  padding: ${Spacing.S4} ${Spacing.S8};

  ${NARROW_TABLE_QUERY} {
    padding: ${Spacing.S2} ${Spacing.S4};
  }
`

export const ReviewEntryLineItemFormItemWrapper = styled.div`
  width: 100%;
  min-width: 0;
`

export const ReviewEntryLineItemCellField = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  min-width: 0;

  input {
    min-width: 0;
  }
`

export const ReviewEntryLineItemFieldLabel = styled.label`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
`

export const ReviewEntryLineItemsAddLineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  min-height: 40px;
`

export const ReviewEntryLineItemsAddLineCell = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  padding: ${Spacing.S4} ${Spacing.S8};
`

export const ReviewEntryLineItemsTotalsWrapper = styled.div`
  display: grid;
  align-items: flex-start;
  grid-template-columns: ${LINE_ITEMS_GRID_TEMPLATE};
  min-width: ${LINE_ITEMS_TABLE_MIN_WIDTH};
  padding: ${Spacing.S12} 0;
  border: 1px solid ${Color.Grey200};
  border-radius: ${BorderRadius.R8};
  background-color: ${Color.White};
`

export const ReviewEntryLineItemsTotalsLabel = styled.div`
  display: flex;
  align-items: flex-start;
  grid-column: 1 / span 3;
  min-width: 0;
  padding: ${Spacing.S8};

  ${NARROW_TABLE_QUERY} {
    padding: ${Spacing.S4};
  }
`

export const ReviewEntryLineItemsTotalsAmount = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: ${Spacing.S2};
  min-width: 0;
  padding: ${Spacing.S8};
  text-align: right;
  overflow: hidden;

  ${NARROW_TABLE_QUERY} {
    padding: ${Spacing.S4};
  }
`
